// =====================================================
// Swiss Finance OS - Impressum
// Rechtlich konforme Impressum-Seite für Schweiz
// =====================================================

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, Phone, MapPin, Globe, Building } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Impressum = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-header-mb">
          <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zur Anwendung
          </Link>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Impressum
          </h1>
          <p className="text-gray-600">
            Angaben gemäß Art. 8 des Schweizer Bundesgesetzes über den Datenschutz (FADP)
          </p>
        </div>

        {/* Firmeninformationen */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Firmeninformationen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">Togeco Buchhaltung</h3>
                <p className="text-gray-600">Schweizer Buchhaltungs- und Steuerberatungsunternehmen</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium">Geschäftsadresse</p>
                      <p className="text-sm text-gray-600">
                        [Straße und Hausnummer]<br/>
                        [PLZ] [Ort]<br/>
                        Schweiz
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Phone className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium">Telefon</p>
                      <p className="text-sm text-gray-600">+41 XX XXX XX XX</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium">E-Mail</p>
                      <p className="text-sm text-gray-600">info@togeco.ch</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Globe className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <p className="font-medium">Website</p>
                      <p className="text-sm text-gray-600">www.togeco.ch</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="font-medium">Handelsregister</p>
                    <p className="text-sm text-gray-600">
                      CHE-XXX.XXX.XXX<br/>
                      Handelsregisteramt [Kanton]
                    </p>
                  </div>
                  
                  <div>
                    <p className="font-medium">UID-Nummer</p>
                    <p className="text-sm text-gray-600">CHE-XXX.XXX.XXX</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Geschäftsführung */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Geschäftsführung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p><strong>Geschäftsführer:</strong> [Name Vorname]</p>
              <p><strong>Stellvertretung:</strong> [Name Vorname]</p>
            </div>
          </CardContent>
        </Card>

        {/* Berufsbezeichnung und Zulassung */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Berufsbezeichnung und Zulassung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Berufsbezeichnung</h4>
                <p className="text-sm text-gray-600">
                  Buchhaltungs- und Steuerberatungsunternehmen nach schweizerischem Recht
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Zulassungsbehörde</h4>
                <p className="text-sm text-gray-600">
                  [Kantonale Zulassungsbehörde]<br/>
                  [Adresse der Behörde]
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Berufsaufsicht</h4>
                <p className="text-sm text-gray-600">
                  [Name der Aufsichtsbehörde]<br/>
                  [Kontaktdaten]
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Haftungsausschluss */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Haftungsausschluss</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Inhalt der Website</h4>
              <p className="text-sm text-gray-600">
                Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. 
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                nach den allgemeinen Gesetzen verantwortlich.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Haftung für Links</h4>
              <p className="text-sm text-gray-600">
                Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen 
                Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber 
                der Seiten verantwortlich.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Urheberrecht</h4>
              <p className="text-sm text-gray-600">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
                dem schweizerischen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und 
                jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
                Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Datenschutz */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Datenschutz</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich. 
              Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder 
              eMail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis.
            </p>
            <p className="text-sm text-gray-600">
              Weitere Informationen zum Datenschutz finden Sie in unserer{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Datenschutzerklärung
              </Link>.
            </p>
          </CardContent>
        </Card>

        {/* Streitschlichtung */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Streitschlichtung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                <a href="https://ec.europa.eu/consumers/odr/" className="text-blue-600 hover:underline ml-1">
                  https://ec.europa.eu/consumers/odr/
                </a>
              </p>
              <p className="text-sm text-gray-600">
                Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
              <p className="text-sm text-gray-600">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              © 2024 Togeco Buchhaltung. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm text-blue-600 hover:underline">
                Datenschutz
              </Link>
              <Link to="/terms" className="text-sm text-blue-600 hover:underline">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impressum;
